// variant-a.jsx — Classic. Mirrors the desktop site's masthead-eyebrow-numbered-sections
// pattern but stacked mobile-first. Ported from /tmp/handoff-fcc-weather/src/variants-ab.jsx::VariantA.

import { useRef, useState } from 'preact/hooks';
import { WxIcon, Glyph } from '../icons.jsx';
import { HeroMedia } from '../hero/hero-media.jsx';
import { PullToRefresh, DayRow, HourCell, MetricChip, SectionHead, Wordmark, AlertBanner, AuthFooter } from '../atoms.jsx';
import { FavoritesStrip } from './shared.jsx';
import {
  getCurrentForState, getHourly, getTenDay, getMetrics,
  getAlert, hasActiveAlert, getLocation, getUpdatedLabel, getSun, getAlmanac, getQuote,
  getRawState, refresh,
} from '../data-bridge.js';

export function VariantA({ brand, density, state, sheetOpen, setSheetOpen, tipFn, heroTreatment = 'subtle', bucket = 'midday', brandKey = 'shared' }) {
  const s = getCurrentForState(state);
  const hourly = getHourly(state);
  const tenDay = getTenDay(state);
  const metrics = getMetrics(state);
  const sun = getSun();
  const almanac = getAlmanac();
  const quote = getQuote();
  const location = getLocation();
  const updated = getUpdatedLabel();
  const max = Math.max(...tenDay.map((d) => d.hi));
  const min = Math.min(...tenDay.map((d) => d.lo));
  const [expanded, setExpanded] = useState(null);
  const scrollRef = useRef(null);
  const showAlert = hasActiveAlert() || state === 'storm' || state === 'rain';
  const alert = getAlert();

  return (
    <PullToRefresh brand={brand} onRefresh={refresh}>
      <div ref={scrollRef} style={{ paddingBottom: 60, color: brand.text }}>
        {/* masthead */}
        <div style={{ position: 'relative', overflow: 'hidden', minHeight: 196 }}>
          <div style={{ position: 'absolute', inset: 0, opacity: heroTreatment === 'punch' ? .85 : .7 }}>
            <HeroMedia state={s.icon === 'partly-cloudy' ? 'cloudy' : s.icon} brand={brand}
                       treatment={heroTreatment === 'punch' ? 'punch' : 'subtle'} motion={heroTreatment !== 'poster'}
                       creditAt="top" creditInset={58} bucket={bucket} brandKey={brandKey}/>
          </div>
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0,
            background: `linear-gradient(180deg, ${brand.bg}33 0%, ${brand.bg}66 50%, ${brand.bg} 100%)`,
            pointerEvents: 'none',
          }}/>
          <div style={{ position: 'relative', padding: `${density.pad + 4}px ${density.pad}px ${density.gap}px`, paddingTop: 76 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Wordmark brand={brand} size={13}/>
              <span style={{ color: brand.border }}>·</span>
              <span style={{ fontFamily: brand.eyebrowFont, fontSize: 11, color: brand.accent, textTransform: brand.icon === 'paw' ? 'none' : 'uppercase', letterSpacing: '.08em' }}>{brand.accentLabel}</span>
            </div>
            <h1 style={{ margin: 0, fontSize: 42, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1, color: brand.text }}>
              {brand.icon === 'paw' ? brand.tagline : 'Weather'}
            </h1>
          </div>
        </div>

        {/* meta strip */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
          padding: `${density.gap}px ${density.pad}px`,
          borderTop: `1px solid ${brand.line}`, borderBottom: `1px solid ${brand.line}`,
          gap: 8, background: brand.panel,
        }}>
          {[
            { k: 'Sun',   v: sun.rise,         sub: 'rise' },
            { k: 'Set',   v: sun.set,          sub: '' },
            { k: 'Day',   v: sun.dayLen,       sub: '' },
            { k: 'Today', v: `${s.hi}°/${s.lo}°`, sub: '' },
          ].map((c) => (
            <div key={c.k}>
              <div style={{ fontSize: 9, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>{c.k}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: brand.text, fontVariantNumeric: 'tabular-nums' }}>{c.v}</div>
            </div>
          ))}
        </div>

        {/* search trigger */}
        <div style={{ padding: `${density.gap}px ${density.pad}px` }}>
          <button onClick={() => setSheetOpen?.(true)} className="wx-tap" style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 14px', borderRadius: 12, background: brand.panel,
            border: `1px solid ${brand.line}`, color: brand.textDim, fontSize: 14,
            textAlign: 'left', cursor: 'pointer',
          }}>
            <Glyph.Search s={16}/>
            Search a city, ZIP, or address…
          </button>
        </div>

        <FavoritesStrip brand={brand} density={density}/>

        {showAlert && <AlertBanner alert={alert} brand={brand} density={density}/>}

        <SectionHead num="Right Now · 01" title={location.label} right={updated} brand={brand} density={density}/>

        {/* hero current */}
        <div style={{ padding: `0 ${density.pad}px ${density.gap}px` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <WxIcon kind={s.icon} size={84}/>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 64 * density.fs, fontWeight: 200, color: brand.text, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {s.temp}<span style={{ fontSize: '.4em', fontWeight: 500, color: brand.textDim, marginLeft: 4 }}>°{getRawState().unit}</span>
              </div>
              <div style={{ fontSize: 15, color: brand.text, marginTop: 4 }}>{s.cond}</div>
              <div style={{ fontSize: 12, color: brand.textDim, marginTop: 2 }}>Feels like {s.feels}° · H {s.hi}° L {s.lo}°</div>
            </div>
          </div>
        </div>

        <SectionHead title="At a glance" brand={brand} density={density}/>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
          padding: `0 ${density.pad}px`,
        }}>
          {metrics.map((m) => <MetricChip key={m.k} m={m} brand={brand} density={density} onLongPress={() => tipFn?.({ tip: m.why, anchor: m.k })}/>)}
        </div>

        <SectionHead num="Next 12 Hours · 02" title="Hourly outlook" right="Updated on load" brand={brand} density={density}/>
        <div style={{ display: 'flex', overflowX: 'auto', borderTop: `1px solid ${brand.line}`, borderBottom: `1px solid ${brand.line}`, background: brand.panel, scrollbarWidth: 'none' }} className="wx-hide-scroll">
          {hourly.map((hr, i) => <HourCell key={i} hr={hr} brand={brand} density={density} peak={i === 0}/>)}
        </div>

        <SectionHead num="The Outlook · 03" title="10-day forecast" right="Range bars scaled" brand={brand} density={density}/>
        <div>
          {tenDay.map((row, i) => <DayRow key={i} row={row} idx={i} max={max} min={min} brand={brand} density={density} expanded={expanded === i} onToggle={() => setExpanded(expanded === i ? null : i)}/>)}
        </div>

        <SectionHead num="Climatology · 04" title="Almanac" right="30-yr archive" brand={brand} density={density}/>
        <div style={{ padding: `0 ${density.pad}px`, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
          {[
            { k: 'Avg high',  v: almanac.avgHi + '°' },
            { k: 'Avg low',   v: almanac.avgLo + '°' },
            { k: 'Record hi', v: almanac.recordHi + '°', sub: almanac.recordHiYear },
            { k: 'Record lo', v: almanac.recordLo + '°', sub: almanac.recordLoYear },
          ].map((a) => (
            <div key={a.k} style={{ background: brand.panel, border: `1px solid ${brand.line}`, borderRadius: 12, padding: 12 }}>
              <div style={{ fontSize: 10, color: brand.textDim, fontFamily: brand.eyebrowFont, textTransform: 'uppercase', letterSpacing: '.06em' }}>{a.k}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: brand.text, fontVariantNumeric: 'tabular-nums' }}>{a.v}</div>
              {a.sub && <div style={{ fontSize: 11, color: brand.textDim }}>{a.sub}</div>}
            </div>
          ))}
        </div>

        <SectionHead title="From the archive" brand={brand} density={density}/>
        <div style={{ margin: `0 ${density.pad}px ${density.pad}px`, padding: `${density.gap}px ${density.pad}px`, borderLeft: `3px solid ${brand.accent}`, background: brand.panel, borderRadius: 8 }}>
          <p style={{ margin: 0, fontSize: 14, color: brand.text, fontStyle: 'italic', lineHeight: 1.5 }}>"{quote.text}"</p>
          <p style={{ margin: '8px 0 0', fontSize: 11, color: brand.textDim, fontFamily: brand.eyebrowFont }}>— {quote.author} · {quote.source}</p>
        </div>

        <AuthFooter brand={brand} density={density}/>

        <div style={{
          padding: `${density.gap}px ${density.pad}px ${density.pad + 20}px`,
          fontSize: 11, color: brand.textDim, textAlign: 'center', lineHeight: 1.6,
        }}>
          Data: Open-Meteo · Alerts: NWS · Radar: RainViewer<br/>
          No ads, no trackers.
        </div>
      </div>
    </PullToRefresh>
  );
}
